
import { supabase } from "@/integrations/supabase/client";
import { CharterSignatory } from '@/types/charter';

/**
 * Enregistre un signataire de la charte républicaine et envoie un email de vérification
 */
export const saveCharterSignatory = async (signatory: Omit<CharterSignatory, 'id' | 'createdAt'>) => {
  console.log('[DEBUG saveCharterSignatory] Début de la fonction avec les données:', signatory);
  
  if (!signatory.firstName || !signatory.lastName || !signatory.email) {
    console.error('[DEBUG saveCharterSignatory] Données manquantes:', signatory);
    throw new Error('Tous les champs sont requis');
  }

  try {
    // Vérifier d'abord si l'email existe déjà
    const { count: existingCount, error: existingError } = await supabase
      .from('charter_signatories')
      .select('*', { count: 'exact', head: true })
      .eq('email', signatory.email);
    
    if (existingError) {
      console.error('[DEBUG saveCharterSignatory] Erreur lors de la vérification d\'email existant:', existingError);
      throw new Error(`Erreur lors de la vérification d'email: ${existingError.message}`);
    } else if (existingCount && existingCount > 0) {
      console.log('[DEBUG saveCharterSignatory] Email déjà utilisé:', signatory.email);
      throw new Error('Cette adresse email a déjà été utilisée pour signer la charte');
    }

    // D'abord, obtenir le nombre actuel de signatures pour définir le nouveau numéro
    const { count: currentCount, error: countError } = await supabase
      .from('charter_signatories')
      .select('*', { count: 'exact', head: true });
    
    if (countError) {
      console.error('[DEBUG saveCharterSignatory] Erreur lors du comptage des signatures:', countError);
      throw new Error(`Erreur lors du comptage des signatures: ${countError.message}`);
    }
    
    // Le nouveau numéro de signature est le nombre actuel de signatures + 1
    const newSignatureNumber = (currentCount || 0) + 1;
    console.log('[DEBUG saveCharterSignatory] Nouveau numéro de signature calculé:', newSignatureNumber);

    const insertData = {
      first_name: signatory.firstName,
      last_name: signatory.lastName,
      email: signatory.email,
      email_verified: false, // Par défaut, l'email n'est pas vérifié
      verification_token: crypto.randomUUID(), // Génère un token unique pour la vérification
      signature_number: newSignatureNumber // Utiliser le numéro calculé au lieu de la séquence auto-incrémentée
    };
    
    console.log('[DEBUG saveCharterSignatory] Tentative d\'insertion dans Supabase avec les données:', insertData);

    const { data, error } = await supabase
      .from('charter_signatories')
      .insert([insertData])
      .select()
      .single();

    console.log('[DEBUG saveCharterSignatory] Réponse de Supabase:', { data, error });

    if (error) {
      console.error('[DEBUG saveCharterSignatory] Erreur Supabase:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });
      
      if (error.code === '23505') { // Code pour violation de contrainte unique
        throw new Error('Cette adresse email a déjà été utilisée pour signer la charte');
      }
      
      throw new Error(`Erreur lors de l'enregistrement : ${error.message}`);
    }
    
    console.log('[DEBUG saveCharterSignatory] Insertion réussie, données insérées:', data);
    
    // Envoi de l'email de vérification via la fonction Edge
    try {
      console.log('[DEBUG saveCharterSignatory] Tentative d\'envoi de l\'email de vérification');
      
      // Utiliser la fonction invoke pour appeler la fonction Edge de manière plus fiable
      const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-verification-email', {
        body: {
          firstName: signatory.firstName,
          lastName: signatory.lastName,
          email: signatory.email,
          signatoryId: data.id,
          verificationToken: data.verification_token,
        }
      });
      
      console.log('[DEBUG saveCharterSignatory] Réponse de la fonction d\'envoi d\'email de vérification:', emailResult);
      
      if (emailError) {
        console.error('[DEBUG saveCharterSignatory] Erreur lors de l\'appel de la fonction d\'envoi d\'email:', emailError);
        // On ne lance pas d'erreur ici pour ne pas empêcher l'enregistrement de la signature
      }
    } catch (emailError) {
      console.error('[DEBUG saveCharterSignatory] Erreur lors de l\'envoi de l\'email de vérification:', emailError);
      // On ne lance pas d'erreur ici pour ne pas empêcher l'enregistrement de la signature
    }
    
    return {
      ...data,
      signature_number: data.signature_number
    };
  } catch (error) {
    console.error('[DEBUG saveCharterSignatory] Erreur attrapée:', error);
    throw error;
  }
}
